import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Select as BaseSelect, SelectProps } from '@mui/base/Select';
import { SVGS } from '../../../assets/images';
import Listbox, { Option } from '../listbox';
import { styled } from '@mui/material';

export type NavigationOption = {
    value: string;
    label: string;
    path: string;
};

interface NavigationSelectorProps {
    options: NavigationOption[];
    label?: string;
    value?: string;
    onSelect?: (option: NavigationOption) => void;
}

const StyledButton = styled('button', { shouldForwardProp: () => true })(
    () => `
        margin: 0;
        background: none;
        border: none;
        width: 100%;
        padding: 0;
        position: relative;
        flex: 1 1 auto;
        min-width: 0;
        box-sizing: border-box;
        gap: 0.95rem; 
        display: flex;
        align-items: center;
        box-shadow: none !important;
        white-space: nowrap;
        
        & div {
            display: flex;
            gap: 8px;
            align-items: center;
            width: 100%;
            min-width: 0;
            color: var(--text-secondary);
            font-size: 26px;
            font-style: normal;
            font-weight: 500;
            line-height: 34px;

            @media (max-width: 1000px){
                font-size: 24px;
                line-height: 32px;
            }

            .navigation-selector-label {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                display: block;
            }
        }
        
        & svg {
            flex-shrink: 0;

            path {
                fill: var(--text-secondary);
            }
        }
        
        font-size: 26px;
        font-style: normal;
        font-weight: 500;
        line-height: 34px;
        @media (max-width: 1000px){
            font-size: 24px;
            line-height: 32px;
        }
    `,
);

const Popup = styled('div')`
    z-index: 1300;
`;

const LimitedListbox = styled(Listbox)`
    max-height: 250px;
    overflow-y: auto;
`;

const CustomButton = React.forwardRef<
    HTMLButtonElement,
    React.PropsWithChildren<any>
    >(function CustomButton({ children, ownerState, ...other }, ref) {
        return (
            <StyledButton type="button" {...other} ref={ref}>
                <div>
                    <span className='navigation-selector-label'>{children}</span>
                    <SVGS.KeyboardArrowDownIcon />
                </div>
            </StyledButton>
        );
    });

export const NavigationSelector: React.FC<NavigationSelectorProps> = ({
    options,
    label = 'Select',
    value,
    onSelect,
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const getCurrentValue = (): string => {
        const currentPath = location.pathname;
        const option = options.find((opt) => currentPath.startsWith(opt.path));
        return option?.value || options[0]?.value || '';
    };

    const selectedValue = value ?? getCurrentValue();

    const handleChange = (
        event: React.MouseEvent | React.KeyboardEvent | React.FocusEvent | null,
        value: string | null
    ) => {
        if (!value) return;
        const option = options.find((opt) => opt.value === value);
        if (option) {
            if (onSelect) {
                onSelect(option);
                return;
            }
            navigate(option.path);
        }
    };

    return (
        <BaseSelect
            value={selectedValue}
            onChange={handleChange}
            slots={{
                root: CustomButton,
                listbox: LimitedListbox,
                popup: Popup,
            }}
        >
            {options.map((option) => (
                <Option key={option.value} value={option.value}>
                    {option.label}
                </Option>
            ))}
        </BaseSelect>
    );
};
